import React, { useMemo, useEffect, useState } from 'react';
import { Provider, useSelector } from 'react-redux';
import { ThemeProvider, CssBaseline, createTheme } from '@mui/material';
import { store } from './redux/store';
import baseTheme from './utils/theme';
import Router from './Router';
import { RootState } from './redux/store';
import { getCurrentUser } from './redux/slices/authSlice';
import { useAppDispatch, useAppSelector } from './redux/hooks';
import LoadingScreen from './components/common/LoadingScreen';
import { cacheService } from './utils/cacheService';

// Configure dayjs for UK locale
import dayjs from 'dayjs';
import 'dayjs/locale/en-gb';
import utc from 'dayjs/plugin/utc';
import timezone from 'dayjs/plugin/timezone';
import customParseFormat from 'dayjs/plugin/customParseFormat';

// Set up dayjs plugins and UK locale
dayjs.extend(utc);
dayjs.extend(timezone);
dayjs.extend(customParseFormat);
dayjs.locale('en-gb'); // Set UK as default locale

// Theme wrapper component that gets the theme mode from Redux
const ThemedApp: React.FC = () => {
  // Use the theme mode from Redux
  const mode = useSelector((state: RootState) => state.theme.mode);
  const dispatch = useAppDispatch();
  const { isLoading, isAuthenticated, user } = useAppSelector((state) => state.auth);
  const [initialLoading, setInitialLoading] = useState(true);
  
  // Fetch user data on app load
  useEffect(() => {
    const fetchUserData = async () => {
      console.log('App mounted, checking for token and fetching user data...');
      
      // Initialize cache service with automatic update
      cacheService.init(() => {
        // Automatically force refresh when update is detected
        console.log('🔄 New version detected - automatically refreshing...');
        cacheService.forceReload();
      });
      
      // Check if we have a token in localStorage
      const token = localStorage.getItem('token');
      if (token) {
        console.log('Token found, attempting to get current user...');
        try {
          // Try to get current user with existing token
          await dispatch(getCurrentUser());
        } catch (error) {
          console.error('Failed to get current user:', error);
        }
      } else {
        console.log('No token found, user needs to login');
      }
      
      setInitialLoading(false);
    };
    
    fetchUserData();
    
    // Cleanup cache service on unmount
    return () => {
      cacheService.stop();
    };
  }, [dispatch]);
  
  // Create a theme instance with the current mode
  const theme = useMemo(() => {
    return createTheme({
      ...baseTheme,
      palette: {
        ...baseTheme.palette,
        mode: mode,
        // Override colors for dark mode
        ...(mode === 'dark' && {
          background: {
            default: '#121212',
            paper: '#1e1e1e',
          },
          text: {
            primary: '#ffffff',
            secondary: 'rgba(255, 255, 255, 0.7)',
            disabled: 'rgba(255, 255, 255, 0.5)',
          },
          divider: 'rgba(255, 255, 255, 0.12)',
        }),
      },
    });
  }, [mode]);
  
  // Show loading screen while fetching user data
  if (initialLoading || (isAuthenticated && isLoading)) {
    return (
      <ThemeProvider theme={theme}>
        <CssBaseline />
        <LoadingScreen fullScreen />
      </ThemeProvider>
    );
  }

  return (
    <ThemeProvider theme={theme}>
      <CssBaseline />
      <Router />
    </ThemeProvider>
  );
};

const App: React.FC = () => {
  return (
    <Provider store={store}>
      <ThemedApp />
    </Provider>
  );
};

export default App;
